import { Play, Pause, StopCircle, Volume2 } from 'lucide-react'
import { Button } from '../ui/button'

interface FloatingAudioControlProps {
  isPlaying: boolean
  isPaused: boolean
  onPlayPause?: () => void
  onPlay?: () => void
  onPause?: () => void
  onStop: () => void
  className?: string
  position?: 'top-right' | 'bottom-right'
}

export function FloatingAudioControl({ 
  isPlaying, 
  isPaused, 
  onPlayPause,
  onPlay,
  onPause,
  onStop,
  className,
  position = 'top-right'
}: FloatingAudioControlProps) {
  // Support both interfaces: onPlayPause (chat-page) or onPlay/onPause (voice-agent)
  const handlePlayPause = () => {
    if (onPlayPause) {
      onPlayPause()
    } else if (isPaused && onPlay) {
      onPlay()
    } else if (!isPaused && onPause) {
      onPause()
    }
  }

  if (!isPlaying && !isPaused) {
    return null
  }

  const positionClasses = position === 'top-right' 
    ? 'fixed top-6 right-6' 
    : 'fixed bottom-24 right-6'

  return (
    <div className={`${positionClasses} z-50 animate-in slide-in-from-bottom-5 duration-300 ${className || ''}`}>
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 rounded-full shadow-2xl border border-white/20 px-4 py-3 flex items-center gap-3 backdrop-blur-xl">
        {/* Audio icon with pulse animation */}
        <div className="relative">
          <Volume2 className="w-5 h-5 text-white" />
          {!isPaused && (
            <span className="absolute inset-0 animate-ping">
              <Volume2 className="w-5 h-5 text-white opacity-75" />
            </span>
          )}
        </div>

        {/* Playing indicator text */}
        <span className="text-sm font-medium text-white hidden sm:inline">
          {isPaused ? 'Audio Paused' : 'Playing Audio...'}
        </span>

        {/* Control buttons */}
        <div className="flex items-center gap-1 ml-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePlayPause}
            className="h-8 w-8 p-0 rounded-full hover:bg-white/20 text-white transition-all hover:scale-110"
            title={isPaused ? 'Resume' : 'Pause'}
          >
            {isPaused ? (
              <Play className="w-4 h-4 fill-white" />
            ) : (
              <Pause className="w-4 h-4" />
            )}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={onStop}
            className="h-8 w-8 p-0 rounded-full hover:bg-red-500/30 text-white transition-all hover:scale-110"
            title="Stop"
          >
            <StopCircle className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
